import React from "react";
import {
    Document, Page, Text, View,
    StyleSheet, Canvas, pdf,
} from "@react-pdf/renderer";

// --- Constantes de Style ---
const BORDER     = "0.5pt solid #000";
const BORDER_CLR = "#000000";
const BORDER_W   = 0.5;
const FONT_SIZE  = 6.5;
const HEADER_H   = 75; 
const HEADER_BG  = "#BDD7EE";
const TOTAL_BG   = "#FFF2CC";
const CHAR_W     = FONT_SIZE * 0.48;
const CELL_PAD   = 4;
const PAGE_W     = 841.89 - 20 * 2; 

const textW = (str) => String(str || "").length * CHAR_W + CELL_PAD;

const computeWidths = (results, ueColumns) => {
    const FIXED_PAD = 10;
    const W_INDEX     = 25;
    const W_MATRICULE = Math.max(textW("Matricule"), ...results.map(r => textW(r.student?.student_number))) + FIXED_PAD;
    const W_NOM       = 100; 
    const W_PRENOM    = 100;
    const W_SEXE      = textW("Sexe") + FIXED_PAD;
    const W_FIXED     = W_INDEX + W_MATRICULE + W_NOM + W_PRENOM + W_SEXE;
    const W_MOY_DATA  = textW("MOYENNE") + FIXED_PAD;

    const rawVar      = [...ueColumns.map(() => 25), W_MOY_DATA];
    const sumRaw      = rawVar.reduce((a, b) => a + b, 0);
    const scale       = (PAGE_W - W_FIXED) / sumRaw;
    const VAR_W       = rawVar.map((w) => w * scale);

    return {
        W_INDEX, W_MATRICULE, W_NOM, W_PRENOM, W_SEXE,
        W_UES:    VAR_W.slice(0, ueColumns.length),
        W_MOYENNE: VAR_W[ueColumns.length],
    };
};

const styles = StyleSheet.create({
    page: { padding: 20, fontSize: FONT_SIZE, fontFamily: "Helvetica", backgroundColor: "#fff" },
    headerRow:       { flexDirection: "row", alignItems: "center", marginBottom: 10 },
    headerTextBlock: { flex: 1, alignItems: "center" },
    headerTitle:     { fontSize: 10, fontFamily: "Helvetica-Bold", textAlign: "center", marginBottom: 2 },
    headerSubtitle:  { fontSize: 8, textAlign: "center", marginBottom: 2 },
    
    infoBox:         { marginTop: 4, alignItems: "center" },
    levelText:       { fontSize: 9, fontFamily: "Helvetica-Bold", color: "#333" },
    examTitle:       { fontSize: 9, fontFamily: "Helvetica-Bold", textDecoration: "underline", marginTop: 2 },

    table:           { width: "100%", borderLeft: BORDER, borderTop: BORDER, marginTop: 5 },
    row:             { flexDirection: "row" },
    cell: { borderRight: BORDER, borderBottom: BORDER, alignItems: "center", justifyContent: "center", paddingHorizontal: 2 },
    cellText:     { fontSize: FONT_SIZE, textAlign: "center" },
    cellTextLeft: { fontSize: FONT_SIZE, textAlign: "left", paddingLeft: 3 },
    cellTextBold: { fontFamily: "Helvetica-Bold" },
    rowEven:      { backgroundColor: "#f9f9f9" },
    
    // FOOTER MODIFIÉ : Aligné à droite pour la date seule
    footer: { 
        marginTop: 15, 
        flexDirection: "row", 
        justifyContent: "flex-end" 
    },
    footerText: { 
        fontSize: 8, 
        fontFamily: "Helvetica-Bold" 
    },
});

const Cell = ({ width, style, children, textStyle, isLeft = false }) => (
    <View style={[styles.cell, { width }, style]}>
        <Text style={[isLeft ? styles.cellTextLeft : styles.cellText, textStyle]}>{children ?? ""}</Text>
    </View>
);

const VerticalHeaderCell = ({ label, width }) => (
    <Canvas 
        style={{ width, height: HEADER_H }} 
        paint={(p, w, h) => {
            p.rect(0, 0, w, h).fill(HEADER_BG);
            p.lineWidth(BORDER_W).strokeColor(BORDER_CLR).rect(0, 0, w, h).stroke();
            p.save();
            p.translate(w / 2, h / 2);
            p.rotate(-90);
            p.fontSize(FONT_SIZE).font("Helvetica-Bold").fillColor("#000")
             .text(label, -(h/2) + 4, -(FONT_SIZE/2), { width: h - 8, align: "center", lineBreak: false });
            p.restore();
        }} 
    />
);

const ProclamationPDFDocument = ({ results = [], ueColumns = [], sessionInfo = null, studyLevelName = "" }) => {
    const widths = results.length > 0 ? computeWidths(results, ueColumns) : null;
    if (!widths) return <Document><Page size="A4"><Text>Aucune donnée</Text></Page></Document>;

    const examLabel = sessionInfo?.evaluation_title || "EXAMEN DE SESSION";
    const levelLabel = studyLevelName || sessionInfo?.level_name || "Niveau d'étude non spécifié";

    return (
        <Document>
            <Page size="A4" orientation="landscape" style={styles.page}>
                <View style={styles.headerRow}>
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerTitle}>UNIVERSITE DES SCIENCES, DES TECHNIQUES ET DES TECHNOLOGIES DE BAMAKO (USTTB)</Text>
                        <Text style={styles.headerTitle}>FACULTE DE MEDECINE ET D'ODONTO-STOMATOLOGIE (FMOS)</Text>
                        <Text style={styles.headerSubtitle}>SESSION : {sessionInfo?.label?.toUpperCase()} — ANNÉE UNIVERSITAIRE : {sessionInfo?.academic_year}</Text>
                        
                        <View style={styles.infoBox}>
                            <Text style={styles.levelText}>CLASSE / NIVEAU : {levelLabel.toUpperCase()}</Text>
                            <Text style={styles.examTitle}>{examLabel.toUpperCase()}</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.table}>
                    <View style={styles.row}>
                        <Cell width={widths.W_INDEX} style={{ backgroundColor: HEADER_BG, height: HEADER_H }} textStyle={styles.cellTextBold}>#</Cell>
                        <Cell width={widths.W_MATRICULE} style={{ backgroundColor: HEADER_BG, height: HEADER_H }} textStyle={styles.cellTextBold}>Matricule</Cell>
                        <Cell width={widths.W_NOM} style={{ backgroundColor: HEADER_BG, height: HEADER_H }} textStyle={styles.cellTextBold}>Nom</Cell>
                        <Cell width={widths.W_PRENOM} style={{ backgroundColor: HEADER_BG, height: HEADER_H }} textStyle={styles.cellTextBold}>Prénom</Cell>
                        <Cell width={widths.W_SEXE} style={{ backgroundColor: HEADER_BG, height: HEADER_H }} textStyle={styles.cellTextBold}>Sexe</Cell>
                        {ueColumns.map((ue, i) => (
                            <VerticalHeaderCell key={i} label={ue.ue_label} width={widths.W_UES[i]} />
                        ))}
                        <VerticalHeaderCell label="MOYENNE" width={widths.W_MOYENNE} />
                    </View>

                    {results.map((r, i) => (
                        <View key={i} style={[styles.row, i % 2 !== 0 ? styles.rowEven : {}]}>
                            <Cell width={widths.W_INDEX}>{i + 1}</Cell>
                            <Cell width={widths.W_MATRICULE} textStyle={styles.cellTextBold}>{r.student?.student_number}</Cell>
                            <Cell width={widths.W_NOM} isLeft={true} textStyle={styles.cellTextBold}>{r.student?.last_name}</Cell>
                            <Cell width={widths.W_PRENOM} isLeft={true}>{r.student?.first_name}</Cell>
                            <Cell width={widths.W_SEXE}>{r.student?.sex}</Cell>
                            {ueColumns.map((ue, idx) => {
                                const found = (r.ue_notes || []).find(n => String(n.ue_id) === String(ue.ue_id));
                                const note = found?.moyenne;
                                return (
                                    <Cell key={idx} width={widths.W_UES[idx]} textStyle={note < 10 ? { color: "red" } : {}}>
                                        {note !== undefined && note !== null ? parseFloat(note).toFixed(2) : "-"}
                                    </Cell>
                                );
                            })}
                            <Cell width={widths.W_MOYENNE} style={{ backgroundColor: TOTAL_BG }} textStyle={styles.cellTextBold}>
                                {parseFloat(r.moyenne || 0).toFixed(2)}
                            </Cell>
                        </View>
                    ))}
                </View>

                {/* FOOTER CORRIGÉ : Uniquement la date, alignée à droite */}
                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>
                        Fait à Bamako, le {new Date().toLocaleDateString("fr-FR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric"
                        })}
                    </Text>
                </View>
            </Page>
        </Document>
    );
};

export async function generateProclamationPDF({ results, ueColumns, sessionInfo, studyLevelName }) {
    const blob = await pdf(
        <ProclamationPDFDocument 
            results={results} 
            ueColumns={ueColumns} 
            sessionInfo={sessionInfo} 
            studyLevelName={studyLevelName} 
        />
    ).toBlob();

    const label = (studyLevelName || sessionInfo?.evaluation_title || "PROCLAMATION").replace(/\s+/g, "_");
    const filename = `PV_${label}.pdf`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
}